import { createElement, lazy, Suspense, useMemo, type ComponentType } from 'react';
import '../../styles/backups.css';
import { registerActions } from '@/lib/actions';
import { useIsAdmin } from '@/lib/hooks';
import { registerAdminSection, registerCommands, registerGlobal, type PaletteCommand } from '@/lib/slots';
import { Loading } from '@/ui/bits';
import type { ClientArea } from '../types';
import { backupNow } from './actions';
import { useOverview } from './api';
import { RestoreReload } from './RestoreReload';

// Backups: the Backups and Backup keys sections of the admin console, "Back up now" for the search palette, and the
// listener that reloads every tab after a restore.

function lazySection(load: () => Promise<{ default: ComponentType }>, label: string): ComponentType {
  const Section = lazy(load);
  return function LazySection() {
    return createElement(Suspense, { fallback: createElement(Loading, { label }) }, createElement(Section));
  };
}

/** The Backups dot: no key, no S3, or the latest backup failed and its retry did not save it. */
function useBackupsAttention(): boolean {
  const { data } = useOverview();
  if (!data) return false;
  const latest = data.latest;
  const failed = latest?.status === 'failed' && latest.retry?.status !== 'succeeded';
  return !data.key || data.destination?.connected === false || failed;
}

function useKeysAttention(): boolean {
  const { data } = useOverview();
  return !!data && !data.key;
}

function useBackupCommands(): PaletteCommand[] {
  const admin = useIsAdmin();
  return useMemo(
    () =>
      admin
        ? [
            {
              id: 'backup-now',
              title: 'Back up now',
              sub: 'Locked with the team key, sent to S3',
              icon: 'database',
              keywords: 'backup restore s3 export',
              order: 140,
              run: backupNow,
            },
          ]
        : [],
    [admin],
  );
}

export const area: ClientArea = {
  name: 'backups',
  setup() {
    registerAdminSection({
      id: 'backups',
      label: 'Backups',
      icon: 'database',
      group: 'Data',
      order: 10,
      component: lazySection(() => import('./BackupsSection'), 'Loading backups'),
      useAttention: useBackupsAttention,
    });
    registerAdminSection({
      id: 'keys',
      label: 'Backup keys',
      icon: 'key',
      group: 'Data',
      order: 20,
      component: lazySection(() => import('./KeysSection'), 'Loading backup keys'),
      useAttention: useKeysAttention,
    });
    registerActions({ backupNow });
    registerCommands(useBackupCommands);
    registerGlobal('backups-restore-reload', RestoreReload);
  },
};
